import { useEffect } from "react";
import { SUPERADMIN_USER_ID } from "constants/constants";
import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import { useUser } from "./context/UserContext";
import { useURLContext } from "./context/URLContext";
import UserLogin from "./pages/user_component/UserLogin";
import Configuration from "./pages/configuration/ConfigurationPage";
import Layout from "./components/layout/Layout";
import LibraryPage from "./pages/library/LibraryPage";
import { BatchContextProvider } from "./context/batch/BatchContext";
import SwaggerPage from "./pages/SwaggerPage";
import { AnalysisPlanContextProvider } from "context/batch/AnalysisPlanContext";
import { KGBatchContextProvider } from "context/batch/KGBatchContext";
import EvaEntityDetailPage from "./pages/eva/EvaEntityDetailPage";
import EvaToolDetailPage from "./pages/eva/EvaToolDetailPage";
import DarwinEvaListPage from "./pages/eva/EvaListPage";
import EvaEntityCreatePage from "./pages/eva/EvaEntityCreatePage";
import ChatPage from "pages/chat/ChatPage";
import AnalystPage from "pages/analyst/AnalystPage";
import ChatSessionPage from "pages/chat/ChatSessionPage";
import { AnalystChatContextProvider } from "context/AnalystChatContext";
import DataConfiguratorContainer from "containers/data_query/DataConfiguratorContainer";
import { init, AuthType } from "@thoughtspot/visual-embed-sdk";
import UserSettingPage from "./pages/user_component/UserSettingPage";
import "katex/dist/katex.min.css";
import ProtectedEvaRoute from "pages/eva/ProtectedEvaRoute";

function App() {
  // Access isLoggedIn and reload function from the user context
  const { userId, isLoggedIn, tsLoginUsername, tsLoginPassword, tsLoginUrl, tsBaseUrl, isLoading, setIsTSInitialized } = useUser();
  const { setPreLoginUrl } = useURLContext();
  const location = useLocation();

  // Login to ThoughtSpot when app loads and user is authenticated
  useEffect(() => {
    const loginToThoughtSpot = async () => {
      // Only proceed if user is logged in and we have ThoughtSpot credentials
      if (!isLoggedIn || !tsLoginUsername || !tsLoginPassword || isLoading) {
        return;
      }
      setIsTSInitialized(false);

      try {
        // Step 1: Login to ThoughtSpot
        const response = await fetch(
          tsLoginUrl,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Accept": "application/json",
              "X-Requested-With": "XMLHttpRequest"
            },
            credentials: "include",
            body: JSON.stringify({
              username: tsLoginUsername,
              password: tsLoginPassword,
              rememberMe: true
            })
          }
        );

        if (!response.ok) {
          console.error("ThoughtSpot login failed: " + response.status + " " + response.statusText);
          return;
        }


        // Step 2: Initialize ThoughtSpot SDK
        init({
          thoughtSpotHost: tsBaseUrl,
          authType: AuthType.None,
        });

        setIsTSInitialized(true);

      } catch (err) {
        console.error("ThoughtSpot login error: " + err);
        setIsTSInitialized(false);
      }
    };

    if (userId === SUPERADMIN_USER_ID) {
      // only login to ThoughtSpot for meritic-admin user
      loginToThoughtSpot();
    }
  }, [isLoggedIn, tsLoginUsername, tsLoginPassword, isLoading, userId]);

  // Store the current URL when user is not logged in (except for root path)
  useEffect(() => {
    if (!isLoggedIn) {
      const fullPath = location.pathname + location.search;
      // Only store if it's not the root path to avoid unnecessary redirects
      if (fullPath !== "/") {
        setPreLoginUrl(fullPath);
      }
    }
  }, [isLoggedIn, location.pathname, location.search, setPreLoginUrl]);

  const authenticatedRoutes = (
    <AnalystChatContextProvider>
      <BatchContextProvider>
        <KGBatchContextProvider>
          <AnalysisPlanContextProvider>
              <Layout>
                <Routes>
                  <Route path="/" element={<ChatPage />} />
                  <Route path="/user" element={<UserSettingPage />} />
                  <Route path="/mkg/*" element={<Configuration />} />
                  <Route path="/chat-session" element={<ChatSessionPage />} />
                  <Route path="/chat/new" element={<ChatPage />} />
                  <Route path="/chat/:id?" element={<ChatPage />} />
                  <Route path="/analyst/:id?" element={<AnalystPage />} />
                  <Route path="/analyst/:id/jobs" element={<AnalystPage />} />
                  <Route path="/analyst/:id/jobs/:jobId" element={<AnalystPage />} />
                  <Route path="/analyst/:id/memory" element={<AnalystPage />} />
                  <Route path="/data-query/:id?" element={<DataConfiguratorContainer />} />
                  <Route path="/docs/swagger" element={<SwaggerPage />} />
                  <Route path="/library" element={<LibraryPage />} />
                  <Route path="/eva" element={<ProtectedEvaRoute><Navigate to="/eva/agents" replace /></ProtectedEvaRoute>} />
                  <Route path="/eva/agents" element={<ProtectedEvaRoute><DarwinEvaListPage /></ProtectedEvaRoute>} />
                  <Route path="/eva/tools" element={<ProtectedEvaRoute><DarwinEvaListPage /></ProtectedEvaRoute>} />
                  <Route path="/eva/playground" element={<ProtectedEvaRoute><DarwinEvaListPage /></ProtectedEvaRoute>} />
                  <Route path="/eva/experiments" element={<ProtectedEvaRoute><DarwinEvaListPage /></ProtectedEvaRoute>} />
                  <Route path="/eva/chat/:id" element={<ProtectedEvaRoute><DarwinEvaListPage /></ProtectedEvaRoute>} />
                  <Route path="/eva/agent/create" element={<ProtectedEvaRoute><EvaEntityCreatePage /></ProtectedEvaRoute>} />
                  <Route path="/eva/skill/create" element={<ProtectedEvaRoute><EvaEntityCreatePage isSkillMode={true} /></ProtectedEvaRoute>} />
                  <Route path="/eva/skills" element={<ProtectedEvaRoute><DarwinEvaListPage /></ProtectedEvaRoute>} />
                  <Route path="/eva/skill/:skill_id" element={<ProtectedEvaRoute><EvaEntityDetailPage isSkillMode={true} /></ProtectedEvaRoute>} />
                  <Route path="/eva/:agent_id" element={<ProtectedEvaRoute><EvaEntityDetailPage /></ProtectedEvaRoute>} />
                  <Route path="/eva/tool/:tool_id" element={<ProtectedEvaRoute><EvaToolDetailPage /></ProtectedEvaRoute>} />
                  <Route path="*" element={<Navigate to="/" />} />{" "}
                </Routes>
              </Layout>
          </AnalysisPlanContextProvider>
        </KGBatchContextProvider>
      </BatchContextProvider>
    </AnalystChatContextProvider>
  );

  const unauthenticatedRoutes = (
    <Routes>
      <Route path="/*" element={<UserLogin />} />{" "}
    </Routes>
  );

  return (
    <div className="App">
      {isLoggedIn ? authenticatedRoutes : unauthenticatedRoutes}
    </div>
  );
}

export default App;